// BookingConfirmationChoice.js - Val av bokningsbekräftelse per bokning (Spår B2/B3)
//
// "[x] Skicka bokningsbekräftelse  ( ) SMS ( ) E-post  Mall: [...]"
// Förvalet kommer från bokningstypen (typens förval och mallar) och annars
// klinikens inställningar - servern har redan löst upp det per typ
// (GET /booking-confirmation-options). Valet kan ändras för just denna bokning.

import React, { useEffect, useState } from 'react';
import { getBookingConfirmationOptions } from './calendarApi';
import './BookingConfirmationChoice.css';

const CHANNEL_LABELS = { sms: 'SMS', email: 'E-post' };
const CONTACT_LABELS = { sms: 'mobilnummer', email: 'e-post' };
const otherChannel = (channel) => (channel === 'sms' ? 'email' : 'sms');

// Hämtar förval och mallar en gång (enabled = false hoppar över hämtningen)
export const useConfirmationOptions = (enabled = true) => {
  const [options, setOptions] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    getBookingConfirmationOptions()
      .then(data => { if (!cancelled) setOptions(data); })
      .catch(err => console.error('Kunde inte hämta bekräftelseval:', err));
    return () => { cancelled = true; };
  }, [enabled]);
  return options;
};

// Förvalet för en bokningstyp ('' / null = utan typ)
export const defaultChoice = (options, typeId) => {
  if (!options) return null;
  const byType = options.defaults_by_type || {};
  const defaults = byType[String(typeId || '')] || byType[''];
  if (!defaults) return null;
  return {
    send: !!defaults.send,
    method: defaults.method || 'sms',
    templates: { ...defaults.templates },
    defaultTemplates: { ...defaults.templates }
  };
};

// Fälten till API:t (send_confirmation/confirmation_method/confirmation_template_id).
// Utan val (options inte laddade) skickas inget → servern använder förvalet.
export const confirmationPayload = (choice, canSend = true) => {
  if (!choice) return {};
  const send = choice.send && canSend;
  return {
    send_confirmation: send,
    confirmation_method: choice.method,
    confirmation_template_id: send ? (choice.templates[choice.method] || null) : null
  };
};

// Kontaktuppgifter för en patient (servern kontrollerar formatet vid utskick)
export const patientContacts = (patient) => ({
  sms: !!(patient && patient.phone && String(patient.phone).trim()),
  email: !!(patient && patient.email && String(patient.email).trim())
});

const BookingConfirmationChoice = ({
  options,
  value,
  onChange,
  patient = null,       // en patient: visa saknad kontaktuppgift
  startTime = null,     // 'YYYY-MM-DDTHH:MM' - redan startad = ingen bekräftelse
  compact = false,
  title = 'Skicka bokningsbekräftelse'
}) => {
  if (!options || !value) {
    return null;
  }

  const contacts = patient ? patientContacts(patient) : null;
  const noContact = contacts && !contacts.sms && !contacts.email;
  const started = !!startTime && new Date(startTime) <= new Date();
  const disabled = noContact || started;
  const checked = value.send && !disabled;
  const templatesFor = (channel) => (channel === 'sms' ? options.sms_templates : options.email_templates) || [];
  const method = value.method;
  const templateId = value.templates[method] || '';
  // Finns en förvald mall (typ → klinik) för kanalen går det inte att välja
  // "ingen" - servern skulle ändå använda förvalet. Utan förval används reservkanalen.
  const hasDefaultTemplate = !!value.defaultTemplates[method];

  const update = (patch) => onChange({ ...value, ...patch });
  const setTemplate = (channel, id) =>
    update({ templates: { ...value.templates, [channel]: id ? parseInt(id, 10) : null } });

  // Vart det faktiskt går: vald kanal, annars reservkanalen
  let notice = null;
  if (started) {
    notice = { kind: 'info', text: 'Tiden har redan startat — ingen bekräftelse skickas.' };
  } else if (noContact) {
    notice = { kind: 'warn', text: 'Patienten saknar både mobilnummer och e-post — ingen bekräftelse kan skickas.' };
  } else if (checked) {
    const other = otherChannel(method);
    const otherTemplate = value.templates[other] || value.defaultTemplates[other];
    if (contacts && !contacts[method]) {
      notice = otherTemplate
        ? { kind: 'info', text: `Patienten saknar ${CONTACT_LABELS[method]} — skickas som ${CHANNEL_LABELS[other].toLowerCase()}.` }
        : { kind: 'warn', text: `Patienten saknar ${CONTACT_LABELS[method]} och det finns ingen ${CHANNEL_LABELS[other].toLowerCase()}-mall — ingen bekräftelse skickas.` };
    } else if (!templateId && !hasDefaultTemplate) {
      notice = otherTemplate
        ? { kind: 'info', text: `Ingen ${CHANNEL_LABELS[method]}-mall vald — skickas som ${CHANNEL_LABELS[other].toLowerCase()}.` }
        : { kind: 'warn', text: 'Ingen bekräftelsemall finns — välj en mall eller lägg in mallar i Kalenderinställningar.' };
    }
  }

  return (
    <div className={`booking-confirmation-choice${compact ? ' compact' : ''}${disabled ? ' disabled' : ''}`}>
      <div className="bcc-row">
        <label className="bcc-send">
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={e => update({ send: e.target.checked })}
          />
          <i className="fas fa-check-circle"></i> {title}
        </label>

        <div className="bcc-channels" role="radiogroup" aria-label="Kanal för bokningsbekräftelse">
          {['sms', 'email'].map(channel => (
            <label key={channel} className="bcc-channel">
              <input
                type="radio"
                name={`bcc-channel-${compact ? 'c' : 'm'}`}
                value={channel}
                checked={method === channel}
                disabled={!checked}
                onChange={() => update({ method: channel })}
              />
              {CHANNEL_LABELS[channel]}
              {contacts && !contacts[channel] && (
                <span className="bcc-missing" title={`Patienten saknar ${CONTACT_LABELS[channel]}`}> (saknas)</span>
              )}
            </label>
          ))}
        </div>

        <label className="bcc-template">
          Mall:
          <select
            value={templateId}
            disabled={!checked}
            onChange={e => setTemplate(method, e.target.value)}
          >
            {!hasDefaultTemplate && (
              <option value="">-- Ingen {CHANNEL_LABELS[method]}-mall --</option>
            )}
            {templatesFor(method).map(t => (
              <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
            ))}
          </select>
        </label>
      </div>

      {notice && (
        <div className={`bcc-notice ${notice.kind}`}>
          <i className={`fas ${notice.kind === 'warn' ? 'fa-exclamation-triangle' : 'fa-info-circle'}`}></i>{' '}
          {notice.text}
        </div>
      )}
    </div>
  );
};

export default BookingConfirmationChoice;
